import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { DatePickerContentEl } from './Content.style';
import { DateUtils, TrackingUtils, withPageContext, Config } from '../../utils';
import { Calendar } from '../Calendar';
import { Icon } from '../Icon';
import { DatePickerLegends } from './DatePickerLegends';
import { ConnectedFaresLoader } from '../Calendar/FaresLoader';

import {
    displayFaresInCalendar,
    getActiveProduct,
    getTripType,
} from '../../redux/currentSearch/selectors';
import {
    CalendarType,
    CalendarTypeProp,
    DateType,
    DateTypeDefault,
    DateTypeProp,
    EntryPoint,
    TabTypeProp,
    TabTypePropDefault,
    TripTypeProp,
} from '../../models';
import { FaresCalendarContext, FaresOnLoadContext } from '../Calendar/Context/FaresCalendarContext';
import {
    getMonthlyDaysWithFaresDeparture,
    getMonthlyDaysWithFaresReturn,
} from '../../redux/flexibleDates/selectors';

const NUM_CALENDARS = 2;

export const DatePickerContent = ({
    dates,
    dateType,
    displayMonth,
    isDatesRangeShown,
    minDate,
    maxDate,
    onSelect,
    onClose,
    userSelectionCallback,
    value,
    flexibleDays,
    displayFaresInCalendar,
    type,
    trackEvent,
    activeProduct,
    tripType,
    monthlyDaysWithFaresDeparture,
    monthlyDaysWithFaresReturn,
}) => {
    const [minMonthSeen, setMinMonthSeen] = useState(DateUtils.getToday);
    const [maxMonthSeen, setMaxMonthSeen] = useState(DateUtils.getToday);
    const [monthToShow, setMonthToShow] = useState(DateUtils.getToday);
    const [isLastFareLoaded, setLastFareLoaded] = useState(false);
    const [disablePrevMonth, setDisablePrevMonth] = useState(false);
    const [disableNextMonth, setDisableNextMonth] = useState(true);

    const leftDate = monthToShow;
    const rightDate = DateUtils.addMonths(leftDate, 1);

    const setMonthState = (date) => {
        const firstDayOfMonth = DateUtils.getFirstDayOfMonth(date);
        const firstDayNextMonth = DateUtils.addMonths(firstDayOfMonth, 1);
        const firstDayLastCalendar = DateUtils.addMonths(firstDayOfMonth, NUM_CALENDARS);
        const currentMonth = DateUtils.getFirstDayOfMonth(DateUtils.getToday());

        setMonthToShow(
            DateUtils.compare(firstDayNextMonth, maxDate) === -1
                ? DateUtils.addMonths(date, -1)
                : date,
        );
        setDisablePrevMonth(DateUtils.compare(firstDayOfMonth, currentMonth) >= 0);
        setDisableNextMonth(DateUtils.compare(firstDayLastCalendar, maxDate) === -1);
    };

    const getLastAvailableDayOfMonth = useCallback(() => {
        return DateUtils.compare(DateUtils.getLastDayOfMonth(rightDate), maxDate) < 0
            ? maxDate
            : DateUtils.getLastDayOfMonth(rightDate);
    }, [maxDate, rightDate]);

    useEffect(() => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        if (activeProduct === EntryPoint.HOTELS) {
            const field = dateType === DateType.FROM ? 'checkin' : 'checkout';
            const label = TrackingUtils.LABEL.fieldOpen(activeProduct, field);
            trackEvent({ action, label });
        } else {
            trackEvent({ action, label: 'open_calendar' });
        }
        setMonthState(displayMonth);
        setMinMonthSeen(displayMonth);
        setMaxMonthSeen(DateUtils.addMonths(displayMonth, 1));
    }, [activeProduct, dateType]);

    useEffect(() => {
        if (DateUtils.getDifferenceInMonths(displayMonth, monthToShow) <= -1) {
            setMonthState(displayMonth);
        }
    }, [displayMonth]);

    useEffect(() => {
        if (isLastFareLoaded) {
            const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
            const pricesAvailableList =
                CalendarType.RETURN === type
                    ? monthlyDaysWithFaresReturn
                    : monthlyDaysWithFaresDeparture;
            const pricesAvailableLeft =
                pricesAvailableList[DateUtils.toString(leftDate).substring(0, 7)] || 0;
            const pricesAvailableRight =
                pricesAvailableList[DateUtils.toString(rightDate).substring(0, 7)] || 0;
            const pricesAvailable = pricesAvailableLeft + pricesAvailableRight;
            const firstAvailableDate =
                DateUtils.compare(leftDate, minDate) > 0 ? minDate : leftDate;
            const lastAvailableDate = getLastAvailableDayOfMonth();
            const daysAvailable =
                DateUtils.getDifferenceInDays(firstAvailableDate, lastAvailableDate) + 1;
            const loadLabel = TrackingUtils.LABEL.loadFaresInCalendarLabel({
                tripType,
                pricesAvailable,
                daysAvailable,
            });
            trackEvent({ action, label: loadLabel });
            setLastFareLoaded(false);
        }
    }, [isLastFareLoaded, getLastAvailableDayOfMonth, rightDate]);

    const nextMonth = () => {
        if (disableNextMonth) {
            return;
        }
        const nextMonthValue = DateUtils.addMonths(monthToShow, 1);
        setMonthState(nextMonthValue);
        if (DateUtils.compare(nextMonthValue, maxMonthSeen) >= 0) {
            setMaxMonthSeen(DateUtils.addMonths(nextMonthValue, 1));
        }
        userSelectionCallback(nextMonthValue);
    };

    const prevMonth = () => {
        if (disablePrevMonth) {
            return;
        }
        const prevMonthValue = DateUtils.addMonths(monthToShow, -1);
        setMonthState(prevMonthValue);
        if (DateUtils.compare(minMonthSeen, prevMonthValue) < 0) {
            setMinMonthSeen(prevMonthValue);
        }
        userSelectionCallback(prevMonthValue);
    };

    const onSelectDate = (date) => {
        onSelect(date, !displayFaresInCalendar);
    };

    const onMoveFocus = () => {
        onClose();
    };

    const getFlexibleDate = (date, minDate) => {
        const diff = DateUtils.getDifferenceInDays(date, minDate);
        return diff >= flexibleDays
            ? DateUtils.add(minDate, Math.floor(diff / flexibleDays) * flexibleDays)
            : minDate;
    };
    const leftFlexibleDate = getFlexibleDate(leftDate, minDate);
    const rightFlexibleDate = getFlexibleDate(DateUtils.addMonths(rightDate, 1), minDate);

    const trackingContext = {
        minMonthSeen: minMonthSeen,
        maxMonthSeen: maxMonthSeen,
        type: type,
    };
    const faresLoadedContext = {
        isLastFareLoaded: isLastFareLoaded,
        setLastFareLoaded: setLastFareLoaded,
        lastDate: getLastAvailableDayOfMonth(),
    };
    const { currencySymbol } = Config.get('i18n').currency;

    return (
        <DatePickerContentEl
            large={currencySymbol.length > 2 && currencySymbol !== '&euro;' && currencySymbol !== '&pound;'}
            className="odf-box odf-box-layer odf-popup odf-popup-flex odf-space-outer-top-xs opened"
        >
            <div className="odf-box-content odf-box-content-lg">
                <div className="odf-row-fluid">
                    <div onClick={prevMonth} style={{ marginTop: 140 }}>
                        <button
                            type="button"
                            className={`odf-btn odf-btn-secondary odf-btn-circle odf-btn-circle-md ${
                                disablePrevMonth ? 'disabled' : ''
                            }`}
                            tabIndex="-1"
                        >
                            <Icon className="odf-icon-bidi" glyph="arrow-left" />
                        </button>
                    </div>
                    <FaresOnLoadContext.Provider value={faresLoadedContext}>
                        <div className="odf-col odf-col-top">
                            <ConnectedFaresLoader date={leftFlexibleDate}>
                                <Calendar
                                    displayMonth={leftDate}
                                    dates={dates}
                                    isDatesRangeShown={isDatesRangeShown}
                                    onSelect={onSelectDate}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    selectedDate={value}
                                    type={type}
                                />
                            </ConnectedFaresLoader>
                        </div>
                        <div className="odf-col odf-col-top">
                            <ConnectedFaresLoader date={rightFlexibleDate}>
                                <Calendar
                                    displayMonth={rightDate}
                                    dates={dates}
                                    isDatesRangeShown={isDatesRangeShown}
                                    onSelect={onSelectDate}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    selectedDate={value}
                                    type={type}
                                />
                            </ConnectedFaresLoader>
                        </div>
                    </FaresOnLoadContext.Provider>
                    <div onClick={nextMonth} style={{ marginTop: 140 }}>
                        <button
                            type="button"
                            className={`odf-btn odf-btn-secondary odf-btn-circle odf-btn-circle-md ${
                                disableNextMonth ? 'disabled' : ''
                            }`}
                            tabIndex="-1"
                        >
                            <Icon className="odf-icon-bidi" glyph="arrow-right" />
                        </button>
                    </div>
                </div>
            </div>
            <div>
                {displayFaresInCalendar && <div className="odf-separator-large" />}
                <FaresCalendarContext.Provider value={trackingContext}>
                    <DatePickerLegends
                        inline
                        displayFaresInCalendar={displayFaresInCalendar}
                        navigateNext={false}
                        onMoveFocus={onMoveFocus}
                        showFaresLegend
                    />
                </FaresCalendarContext.Provider>
            </div>
        </DatePickerContentEl>
    );
};

DatePickerContent.propTypes = {
    dates: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
    dateType: DateTypeProp.isRequired,
    displayMonth: PropTypes.instanceOf(Date).isRequired,
    isDatesRangeShown: PropTypes.bool.isRequired,
    minDate: PropTypes.instanceOf(Date).isRequired,
    maxDate: PropTypes.instanceOf(Date).isRequired,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    userSelectionCallback: PropTypes.func.isRequired,
    value: PropTypes.instanceOf(Date),
    flexibleDays: PropTypes.number,
    displayFaresInCalendar: PropTypes.bool.isRequired,
    type: CalendarTypeProp.isRequired,
    trackEvent: PropTypes.func.isRequired,
    activeProduct: TabTypeProp,
    tripType: TripTypeProp.isRequired,
    monthlyDaysWithFaresDeparture: PropTypes.object.isRequired,
    monthlyDaysWithFaresReturn: PropTypes.object.isRequired,
};

DatePickerContent.defaultProps = {
    value: null,
    activeProduct: TabTypePropDefault,
    dateType: DateTypeDefault,
};

const mapStateToPropsSelectorRender = createStructuredSelector({
    activeProduct: getActiveProduct,
    displayFaresInCalendar,
    tripType: getTripType,
    monthlyDaysWithFaresDeparture: getMonthlyDaysWithFaresDeparture,
    monthlyDaysWithFaresReturn: getMonthlyDaysWithFaresReturn,
});

const DatePickerContentWithPageContext = withPageContext(DatePickerContent);
export const ConnectedDatePickerContent = connect(mapStateToPropsSelectorRender)(
    DatePickerContentWithPageContext,
);
